import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useState } from 'react';
import { client } from '../../util/api-client';
import type { Order, OrderSearchInput } from '@repo/api-contract';
import { debounce } from '@mui/material/utils';
import { pushToast, useDispatch } from '../../state';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../modals/constants/errors';

interface Props {
  label: string;
  value: string;
  color: 'primary' | 'success' | 'error';
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFetch: (order: Order | null) => void;
}

const SearchOrder: React.FC<Props> = ({
  label,
  value,
  color,
  error,
  helperText,
  disabled,
  onChange,
  onFetch,
}) => {
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const searchOrders = async (searchQuery: string): Promise<Order | null> => {
    try {
      const payload: OrderSearchInput = {
        query: searchQuery,
        searchBy: 'orderCode',
        size: 5,
        page: 0,
      };

      const resp = await client.searchOrders.query(payload);

      // Find an order whose code matches exactly (case-insensitive)
      const foundOrder = resp.results.find(
        (ord) => ord.code.toLowerCase() === searchQuery.toLowerCase(),
      );
      // If found, return it; otherwise return null
      return foundOrder ?? null;
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      return null;
    }
  };

  const handleBlur = async () => {
    setLoading(true);
    // If empty, user wants to create from scratch.
    if (!value.trim()) {
      setLoading(false);
      return;
    }

    const order = await searchOrders(value);

    onFetch(order);
    setLoading(false);
  };

  return (
    <TextField
      color={color}
      focused
      onChange={onChange}
      disabled={disabled}
      error={error}
      helperText={helperText}
      label={label}
      value={value}
      onBlur={handleBlur}
      InputProps={{
        endAdornment: (
          <>{loading ? <CircularProgress color="inherit" size={20} /> : null}</>
        ),
      }}
    />
  );
};

export default SearchOrder;
